import React, { useEffect, useRef, useState } from 'react';
import { Box, Heading, Text, Flex, IconButton, Spacer, HStack, Input, useOutsideClick, useToast } from '@chakra-ui/react';
import { FiTrash2, FiEdit3, FiCheck, FiX } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

const CampaignCard = ({ campaign, onDelete, onRename }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const editContainerRef = useRef(null);
  const inputRef = useRef(null);
  const blockNavigationRef = useRef(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  
  // Safely extract properties with fallbacks
  const id = campaign?.id || campaign?._id || '';
  const name = campaign?.name || 'Untitled Campaign';
  const createdAt = campaign?.createdAt || null;
  const updatedAt = campaign?.updated_at || campaign?.updatedAt || campaign?.lastUpdated || createdAt;
  const profile = campaign?.profile || {};
  const prospects = campaign?.prospects || [];
  
  // Safely format date
  const formattedDate = updatedAt 
    ? format(new Date(updatedAt), 'MMM d, yyyy')
    : 'Unknown date';
  
  const prospectCount = prospects?.length || 0;
  /** Aligned with VirtualizedPersonaResults: locked once exported / sent to sequencer. */
  const isCampaignLocked =
    Boolean(campaign?.is_locked ?? campaign?.isLocked);

  const hoverStyles = isEditing
    ? {}
    : {
        transform: 'translateY(-2px)',
        cursor: 'pointer',
        borderColor: 'rgba(255,255,255,0.22)',
      };

  useEffect(() => {
    if (!isEditing) {
      setDraftName(name);
    }
  }, [name, isEditing]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleCardClick = (event) => {
    if (blockNavigationRef.current) {
      event?.stopPropagation();
      blockNavigationRef.current = false;
      return;
    }

    if (isEditing) {
      event?.stopPropagation();
      return;
    }

    navigate(`/campaign/${id}`);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    if (isEditing || isCampaignLocked) {
      return;
    }
    onDelete(id);
  };

  const handleRenameClick = (e) => {
    e.stopPropagation();
    blockNavigationRef.current = false;
    setDraftName(name);
    setIsEditing(true);
  };

  const handleCancel = (e, { blockNextNavigation = false } = {}) => {
    if (e) {
      e.stopPropagation();
      if (e.preventDefault) {
        e.preventDefault();
      }
    }
    setDraftName(name);
    setIsEditing(false);
    blockNavigationRef.current = blockNextNavigation;
  };

  const handleNameChange = (e) => {
    setDraftName(e.target.value);
  };

  const handleRenameSubmit = async () => {
    const trimmedName = draftName.trim();

    if (!trimmedName) {
      toast({
        title: 'Campaign name required',
        description: 'Please enter a campaign name before saving.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (trimmedName === name.trim()) {
      setIsEditing(false);
      return;
    }

    if (!onRename) {
      setIsEditing(false);
      return;
    }

    try {
      setIsSaving(true);
      await onRename(id, trimmedName);
      toast({
        title: 'Campaign renamed',
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to rename campaign:', error);
      toast({
        title: 'Rename failed',
        description: error?.message || error?.response?.data?.detail || 'Unable to rename campaign.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputKeyDown = async (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      await handleRenameSubmit();
    } else if (e.key === 'Escape') {
      handleCancel(e);
    }
  };

  useOutsideClick({
    ref: editContainerRef,
    handler: () => {
      if (isEditing && !isSaving) {
        handleCancel(undefined, { blockNextNavigation: true });
      }
    },
  });
  
  // Handle rendering errors gracefully
  try {
    return (
      <Box
        p={4}
        bg="#0f0f0f"
        borderRadius="12px"
        border="1px solid rgba(255,255,255,0.10)"
        transition="all 0.2s"
        _hover={hoverStyles}
        onClick={handleCardClick}
        role="group"
        cursor={isEditing ? 'default' : 'pointer'}
      >
        <Flex align="center" mb={2}>
          {!isEditing ? (
            <>
              <Heading size="md" noOfLines={1} color="#fafafa" fontWeight="600" letterSpacing="-0.01em">
                {name}
              </Heading>
              <Spacer />
              <HStack spacing={1}>
                <IconButton
                  icon={<FiEdit3 size={14} />}
                  variant="ghost"
                  aria-label="Rename campaign"
                  size="sm"
                  color="#a3a3a3"
                  onClick={handleRenameClick}
                  opacity={0}
                  pointerEvents="none"
                  _groupHover={{ opacity: 1, pointerEvents: 'auto' }}
                  _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }}
                  _focus={{ boxShadow: 'none' }}
                />
                {!isCampaignLocked && (
                  <IconButton
                    icon={<FiTrash2 size={14} />}
                    variant="ghost"
                    aria-label="Delete campaign"
                    size="sm"
                    color="#a3a3a3"
                    onClick={handleDeleteClick}
                    opacity={0}
                    pointerEvents="none"
                    _groupHover={{ opacity: 1, pointerEvents: 'auto' }}
                    _focus={{ opacity: 1, pointerEvents: 'auto', boxShadow: 'none' }}
                    _hover={{ color: 'rgba(239,68,68,0.85)', bg: 'rgba(239,68,68,0.08)' }}
                  />
                )}
              </HStack>
            </>
          ) : (
            <HStack
              ref={editContainerRef}
              spacing={2}
              align="center"
              w="100%"
              onClick={(e) => e.stopPropagation()}
            >
              <Input
                ref={inputRef}
                value={draftName}
                onChange={handleNameChange}
                onKeyDown={handleInputKeyDown}
                size="sm"
                flex={1}
                bg="#171717"
                borderColor="rgba(255,255,255,0.12)"
                color="#fafafa"
                fontSize="sm"
                _placeholder={{ color: 'rgba(255,255,255,0.25)' }}
                isDisabled={isSaving}
                _focus={{ borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none' }}
                _hover={{ borderColor: 'rgba(255,255,255,0.2)' }}
              />
              <IconButton
                icon={<FiCheck size={14} />}
                variant="solid"
                aria-label="Save campaign name"
                size="sm"
                bg="#fafafa"
                color="#0a0a0a"
                _hover={{ opacity: 0.85 }}
                _focus={{ boxShadow: 'none' }}
                onClick={async (e) => {
                  e.stopPropagation();
                  await handleRenameSubmit();
                }}
                isLoading={isSaving}
              />
              <IconButton
                icon={<FiX size={14} />}
                variant="ghost"
                aria-label="Cancel renaming"
                size="sm"
                color="#a3a3a3"
                _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }}
                _focus={{ boxShadow: 'none' }}
                onClick={handleCancel}
                isDisabled={isSaving}
              />
            </HStack>
          )}
        </Flex>

        <Text color="#a3a3a3" fontSize="xs" mb={2}>
          Last updated {formattedDate}
        </Text>

        <Flex wrap="wrap" gap={2} mb={3}>
          {profile?.ideal_customers?.length > 0 && (
            <Box
              px={2}
              py="2px"
              borderRadius="full"
              bg="rgba(255,255,255,0.06)"
              border="1px solid rgba(255,255,255,0.12)"
            >
              <Text fontSize="10px" fontWeight="600" color="rgba(255,255,255,0.55)" letterSpacing="0.06em" textTransform="uppercase">
                {profile.ideal_customers.length} Profiles
              </Text>
            </Box>
          )}
        </Flex>

        {profile?.summary && (
          <Text noOfLines={2} fontSize="sm" color="#a3a3a3">
            {profile.summary}
          </Text>
        )}
      </Box>
    );
  } catch (error) {
    console.error("Error rendering campaign card:", error);
    return (
      <Box
        p={5}
        bg="#0f0f0f"
        borderRadius="12px"
        border="1px solid rgba(239,68,68,0.15)"
      >
        <Heading size="md" color="rgba(239,68,68,0.85)">Error Displaying Campaign</Heading>
        <Text mt={2} color="#a3a3a3" fontSize="sm">There was a problem loading this campaign.</Text>
      </Box>
    );
  }
};

export default CampaignCard; 